import { authClient } from "@features/auth/authClient";
import { UserInfo } from "@features/auth/useAuth";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { Alert, AlertDescription, AlertTitle } from "@shadcn/ui/alert";
import { Button } from "@shadcn/ui/button";
import { useRouter } from "@tanstack/react-router";
import { startCase } from "es-toolkit";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";
import { InvitationPageFragment$key } from "./__generated__/InvitationPageFragment.graphql";

const fragment = graphql`
	fragment InvitationPageFragment on Invitation {
		id
		email
		role @required(action: THROW)
		organizationName
		inviterName
	}
`;

type InvitationPageProps = {
	user: UserInfo;
	invitation: InvitationPageFragment$key;
};

export const InvitationPage = (props: InvitationPageProps) => {
	const { user } = props;
	const router = useRouter();
	const { id, email, inviterName, role, organizationName } = useFragment(fragment, props.invitation);
	const LL = useI18nContext().LL.auth;

	const isCurrentUser = email === user.email;
	if (!isCurrentUser) {
		return (
			<div className="flex justify-center items-center h-screen">
				<Alert className="w-fit">
					<AlertTitle className="text-3xl">
						<T>{LL.INVITATION_PAGE_TITLE()}</T>
					</AlertTitle>
					<AlertDescription>
						<p className="text-base">
							<T>{LL.INVITATION_PAGE_WRONG_EMAIL_TEXT({ email, currentEmail: user.email })}</T>
						</p>
						<div className="flex justify-end gap-2 mt-2 w-full">
							<Button
								variant="destructive"
								onClick={async () => {
									await authClient.signOut();
									router.navigate({ to: "/auth" });
								}}
							>
								<T>{LL.SIGN_OUT()}</T>
							</Button>
						</div>
					</AlertDescription>
				</Alert>
			</div>
		);
	}
	return (
		<div className="flex justify-center items-center h-screen">
			<Alert className="w-fit">
				<AlertTitle className="text-3xl">
					<T>{LL.INVITATION_PAGE_TITLE()}</T>
				</AlertTitle>
				<AlertDescription>
					<p className="text-base">
						<T>{LL.INVITATION_PAGE_COPY({ organization: organizationName, inviter: inviterName, role: startCase(role) })}</T>
					</p>
					<div className="flex justify-end gap-2 mt-2 w-full">
						<Button
							variant="outline"
							onClick={async () => {
								await authClient.organization.rejectInvitation({
									invitationId: id,
								});
								await authClient.signOut();
								router.navigate({ to: "/auth" });
							}}
						>
							<T>{LL.INVITATION_PAGE_REJECT_BUTTON()}</T>
						</Button>
						<Button
							onClick={async () => {
								await authClient.organization.acceptInvitation({
									invitationId: id,
								});
								router.navigate({ to: "/" });
							}}
						>
							<T>{LL.INVITATION_PAGE_ACCEPT_BUTTON()}</T>
						</Button>
					</div>
				</AlertDescription>
			</Alert>
		</div>
	);
};
